import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./button";

type InputVariant = "default" | "accent";

type InputProps = Omit<React.ComponentProps<"input">, "size"> & {
  size?: "default" | "lg" | "sm";
  asChild?: boolean;
  variant?: InputVariant;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size = "default", asChild, variant = "default", ...props }, ref) => {
    const Comp = asChild ? Slot : "input";
    return (
      <Comp
        ref={ref}
        type={type}
        data-slot="input"
        className={cn(
          "file:text-foreground border border-accent placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex w-full min-w-0 rounded-lg px-3 py-1 transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 text-sm [@media(hover:none)_and_(pointer:coarse)]:text-base",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
          size === "default" ? "h-10" : size === "lg" ? "h-11" : "h-9",
          variant === "accent" ? "bg-background" : "bg-accent",
          className
        )}
        {...props}
      />
    );
  }
);

Input.displayName = "Input";

const PasswordInput = React.forwardRef<
  HTMLInputElement,
  InputProps & { containerClassName?: string }
>(
  (
    {
      className,
      containerClassName,
      placeholder = "•••••••••••••••••••••",
      type = "password",
      ...props
    },
    ref
  ) => {
    const [inputType, setInputType] = React.useState(type);

    const handleClickShowPassword = React.useCallback(() => {
      setInputType(inputType === "password" ? "text" : "password");
    }, [inputType]);

    return (
      <div className={cn("relative", containerClassName)}>
        <Input
          ref={ref}
          type={inputType}
          placeholder={placeholder}
          className={cn(className, "pr-10")}
          {...props}
        />
        <Button
          variant="ghost"
          size="icon-sm"
          type="button"
          className="size-6 absolute right-2 rounded-full top-1/2 -translate-y-1/2 [&_svg]:size-4 [&_svg]:text-muted-foreground"
          onClick={handleClickShowPassword}
        >
          {inputType === "password" ? <EyeOffIcon /> : <EyeIcon />}
        </Button>
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export { Input, PasswordInput, type InputProps, type InputVariant };
